import { useCallback, useMemo, useState } from "react"
import {
	Download,
	Eye,
	Lock,
	Maximize2,
	Paperclip,
	Pencil,
	UserMinus,
	UserRound,
	UserRoundPlus,
	X
} from "lucide-react"
import { BODY_LIMIT, errorMessage, mediaUrl } from "../../api/client"
import { accent } from "../../design/palette"
import { useApp } from "../../state/AppState"
import { Avatar, Button, Textarea, cx } from "../ui"
import { RichTextEditor } from "../editor/RichTextEditor"
import { RichBody } from "../editor/RichBody"
import { ImageLightbox } from "../editor/ImageLightbox"
import { AttachmentPreview, canPreview } from "./AttachmentPreview"
import { fileSize, fullTimestamp, fullTimestampWithOffset, relativeTime } from "../../lib/format"

/**
 * One entry in a thread - the issue itself, a comment, or a system entry.
 *
 * The lock is shown, not just enforced. A frozen entry says so, because the
 * whole point of the product is that nobody can quietly rewrite what they said
 * once the other side has replied to it.
 */
export function EntryCard({
	id,
	projectId,
	issueId,
	entry,
	author,
	subject,
	isRoot = false,
	canEdit = false,
	onSave,
	className
}) {
	const { timezone } = useApp()
	const kind = entry.kind || "comment"
	const tone = accent(author?.colour)

	if (kind !== "comment") {
		return <SystemEntry entry={entry} author={author} subject={subject} timezone={timezone} />
	}

	return (
		<article
			id={id}
			className={cx(
				"rounded-card border border-ink-200/80 border-l-[3px] bg-white shadow-card",
				tone.rail,
				className
			)}
		>
			<EntryByline author={author} entry={entry} timezone={timezone} className="px-3.5 pt-3" />
			<EntryBody
				projectId={projectId}
				issueId={issueId}
				entry={entry}
				isRoot={isRoot}
				canEdit={canEdit}
				onSave={onSave}
				className="px-3.5 pb-3.5 pt-2"
			/>
		</article>
	)
}

/** Who wrote it and when. Shared so the opening post can lead with the same line. */
export function EntryByline({ author, entry, timezone, className }) {
	return (
		<header className={cx("flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
			<Avatar person={author} size="sm" />
			<span className="text-[0.8125rem] font-semibold text-ink-900">
				{author?.name || "Unknown"}
			</span>
			<span className="text-2xs text-ink-500">
				{[author?.role, author?.company].filter(Boolean).join(", ")}
			</span>
			<span
				className="ml-auto shrink-0 text-2xs text-ink-400"
				title={fullTimestampWithOffset(entry.created_at, timezone)}
			>
				{fullTimestamp(entry.created_at, timezone)}
				{entry.edited_at && " · edited"}
			</span>
		</header>
	)
}

/**
 * The text of one entry, with its editor and its lock notice.
 *
 * Split out from the card so the issue page can put the opening post directly
 * under the title rather than in a card of its own. A question and the detail
 * behind it are one thought, and two boxes made them read as two.
 */
export function EntryBody({
	projectId,
	issueId,
	entry,
	isRoot = false,
	canEdit = false,
	onSave,
	className
}) {
	const { toast } = useApp()
	const [editing, setEditing] = useState(false)
	const [body, setBody] = useState(entry.body || "")
	const [attachments, setAttachments] = useState(entry.attachments || [])
	const [saving, setSaving] = useState(false)
	// Markdown comes from an agent through the API. The rich-text editor would
	// turn it into HTML, so it is edited as the source it was written as.
	const isMarkdown = entry.body_format === "markdown"

	// A screenshot pasted into the body is rendered there, so showing it again
	// in the gallery underneath would duplicate it.
	const galleryFiles = useMemo(() => {
		const text = entry.body || ""
		const embedded = new Set([
			...[...text.matchAll(/data-attachment-id="([^"]+)"/g)].map((m) => m[1]),
			...[...text.matchAll(/\/attachments\/([\w-]+)/g)].map((m) => m[1])
		])
		return (entry.attachments || []).filter((f) => !embedded.has(f.attachment_id))
	}, [entry.body, entry.attachments])

	async function save() {
		setSaving(true)
		try {
			// The format is fixed when the entry is written; an edit sends text only.
			await onSave(
				isMarkdown
					? { body }
					: { body, attachment_ids: attachments.map((a) => a.attachment_id) }
			)
			setEditing(false)
		} catch (cause) {
			toast(errorMessage(cause), "error")
		} finally {
			setSaving(false)
		}
	}

	return (
		<div className={className}>
			{editing ? (
				<div className="space-y-2.5">
					{isMarkdown ? (
						<div>
							<Textarea
								value={body}
								onChange={(event) => setBody(event.target.value)}
								rows={12}
								autoFocus
								onKeyDown={(event) => {
									if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
										event.preventDefault()
										if (!saving) save()
									}
								}}
								aria-label="Revise your entry (Markdown)"
								className="font-mono text-[0.8125rem] leading-relaxed"
							/>
							<p className="mt-1 flex justify-between gap-3 text-2xs text-ink-400">
								<span>Markdown - saved as written.</span>
								<span
									className={cx(
										"tabular",
										body.length > BODY_LIMIT && "font-medium text-rose-600"
									)}
								>
									{body.length.toLocaleString()} / {BODY_LIMIT.toLocaleString()}
								</span>
							</p>
						</div>
					) : (
						<RichTextEditor
							projectId={projectId}
							issueId={issueId}
							value={body}
							onChange={setBody}
							attachments={attachments}
							onAttachmentsChange={setAttachments}
							placeholder="Revise your entry"
							autoFocus
							onSubmit={() => !saving && save()}
						/>
					)}
					<div className="flex items-center justify-end gap-2">
						<Button size="sm" icon={X} onClick={() => setEditing(false)}>
							Cancel
						</Button>
						<Button
							size="sm"
							variant="primary"
							onClick={save}
							title="Ctrl+Enter"
							busy={saving}
							disabled={saving}
						>
							Save changes
						</Button>
					</div>
				</div>
			) : (
				<>
					{entry.body ? (
						<RichBody body={entry.body} format={entry.body_format} />
					) : (
						<p className="text-[0.8125rem] italic text-ink-400">No detail added.</p>
					)}

					{galleryFiles.length > 0 && <AttachmentList attachments={galleryFiles} />}

					<div className="mt-2.5 flex items-center gap-2">
						{canEdit ? (
							<>
								<Button
									size="sm"
									variant="ghost"
									icon={Pencil}
									onClick={() => {
										setBody(entry.body || "")
										setAttachments(entry.attachments || [])
										setEditing(true)
									}}
								>
									Edit
								</Button>
								<span className="text-2xs text-ink-400">
									Editable until someone replies
								</span>
							</>
						) : (
							<span className="inline-flex items-center gap-1.5 text-2xs text-ink-400">
								<Lock size={11} aria-hidden />
								{isRoot ? "Issue locked" : "Locked"} — the thread has moved on
							</span>
						)}
						{isMarkdown && (
							<span
								className="ml-auto rounded bg-ink-100 px-1.5 py-0.5 text-2xs font-medium text-ink-500"
								title="Written in Markdown, through the API"
							>
								Markdown
							</span>
						)}
					</div>
				</>
			)}
		</div>
	)
}

/**
 * An assignment or a join, rendered inline in the thread.
 *
 * Deliberately quieter than a comment but still part of the record: who handed
 * the issue to whom, and when, belongs in the history rather than in metadata
 * off to one side.
 */
function SystemEntry({ entry, author, subject, timezone }) {
	// Taking an issue on yourself is a normal move - the last speaker saying
	// "leave it with me". It should not read as asking yourself a question.
	const selfAssigned = entry.author_id && entry.author_id === entry.subject_id

	const config = {
		assigned: {
			icon: UserRound,
			className: "text-state-open",
			text: selfAssigned ? (
				<>
					<strong className="font-semibold text-ink-800">{author?.name}</strong> took this
					on
				</>
			) : (
				<>
					<strong className="font-semibold text-ink-800">{author?.name}</strong> asked{" "}
					<strong className="font-semibold text-ink-800">{subject?.name}</strong> to
					respond
				</>
			)
		},
		unassigned: {
			icon: UserMinus,
			className: "text-ink-400",
			text: (
				<>
					<strong className="font-semibold text-ink-800">{author?.name}</strong> cleared
					the assignment
				</>
			)
		},
		joined: {
			icon: UserRoundPlus,
			className: "text-ink-400",
			text: (
				<>
					<strong className="font-semibold text-ink-800">{author?.name}</strong> added{" "}
					<strong className="font-semibold text-ink-800">{subject?.name}</strong> to this
					issue
				</>
			)
		}
	}[entry.kind] || { icon: UserRound, className: "text-ink-400", text: entry.kind }

	const Icon = config.icon

	return (
		<div className="flex items-start gap-2.5 px-3.5 py-1">
			<span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-ink-100">
				<Icon size={11} className={config.className} aria-hidden />
			</span>
			<div className="min-w-0 flex-1">
				<p className="text-2xs leading-relaxed text-ink-600">
					{config.text}
					<span
						className="ml-1.5 text-ink-400"
						title={fullTimestampWithOffset(entry.created_at, timezone)}
					>
						· {relativeTime(entry.created_at, timezone)}
					</span>
				</p>
				{entry.body && (
					<div className="mt-1 rounded-md bg-ink-50 px-2.5 py-1.5">
						<RichBody
							body={entry.body}
							format={entry.body_format}
							className="text-[0.8125rem]"
						/>
					</div>
				)}
			</div>
		</div>
	)
}

/** The file as a download, even where an image would otherwise open in place. */
function downloadUrl(file) {
	const separator = file.url.includes("?") ? "&" : "?"
	return mediaUrl(`${file.url}${separator}download=1`)
}

function AttachmentList({ attachments }) {
	const images = attachments.filter((file) => file.is_image)
	const others = attachments.filter((file) => !file.is_image)
	const [previewing, setPreviewing] = useState(null)
	const [viewing, setViewing] = useState(null)
	const closeViewer = useCallback(() => setViewing(null), [])

	return (
		<div className="mt-2.5 space-y-2">
			{images.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{images.map((file) => (
						<div
							key={file.attachment_id}
							className="group relative overflow-hidden rounded-md border border-ink-200 transition hover:border-brand-400"
							title={`${file.filename} — ${fileSize(file.size_bytes)}`}
						>
							<button
								type="button"
								onClick={() => setViewing(file)}
								className="block cursor-zoom-in"
								aria-label={`View ${file.filename}`}
							>
								<img
									src={mediaUrl(file.url)}
									alt={file.filename}
									className="h-24 w-auto max-w-[14rem] object-cover"
								/>
							</button>
							{/* Shown on hover, and on keyboard focus for those not using a mouse. */}
							<div className="keep-light pointer-events-none absolute inset-0 flex items-center justify-center gap-1.5 bg-ink-950/45 opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
								<button
									type="button"
									onClick={() => setViewing(file)}
									className="inline-flex h-7 items-center gap-1 rounded-md bg-white/95 px-2 text-2xs font-semibold text-ink-900 shadow-card transition-colors hover:bg-white"
								>
									<Maximize2 size={12} aria-hidden />
									View
								</button>
								<a
									href={downloadUrl(file)}
									download={file.filename}
									className="inline-flex h-7 items-center gap-1 rounded-md bg-white/95 px-2 text-2xs font-semibold text-ink-900 shadow-card transition-colors hover:bg-white"
								>
									<Download size={12} aria-hidden />
									Download
								</a>
							</div>
						</div>
					))}
				</div>
			)}

			{others.map((file) => (
				<div key={file.attachment_id} className="flex max-w-full flex-wrap items-center gap-1.5">
					<a
						href={mediaUrl(file.url)}
						target="_blank"
						rel="noreferrer"
						className="inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-ink-200 px-2.5 py-1.5 text-[0.8125rem] transition-colors hover:border-brand-400 hover:bg-brand-50"
					>
						<Paperclip size={13} className="shrink-0 text-ink-400" aria-hidden />
						<span className="min-w-0 truncate font-medium text-ink-800">
							{file.filename}
						</span>
						<span className="shrink-0 text-2xs text-ink-500">
							{fileSize(file.size_bytes)}
						</span>
						<Download size={12} className="shrink-0 text-ink-400" aria-hidden />
					</a>
					{canPreview(file) && (
						<Button size="sm" variant="ghost" icon={Eye} onClick={() => setPreviewing(file)}>
							Preview
						</Button>
					)}
				</div>
			))}

			<AttachmentPreview file={previewing} onClose={() => setPreviewing(null)} />
			<ImageLightbox
				src={viewing ? mediaUrl(viewing.url) : null}
				filename={viewing?.filename}
				onClose={closeViewer}
			/>
		</div>
	)
}
